// @flow
/* eslint-disable no-unused-vars */

import { IllegalStateException } from "../../../exceptions";

import type { ILayoutHintRule, AttributeType } from "../../types";
import type AttributeCollection from "../AttributeCollection";
import type AttributeModel from "../AttributeModel";

/**
 * Abstract class for layout hint rules
 * @abstract
 */
class BaseLayoutHintRule implements ILayoutHintRule {
  /**
   * Indicates if the rule implements the isApplicableForHint method
   * @type {boolean}
   * @default false
   */
  static checkHintApplicability: boolean = false;

  /**
   * Indicates if the rule instance is applicable for a specific attribute<br>
   */
  static isApplicableForAttribute(attribute: AttributeType): boolean {
    return true;
  }

  /**
   * Indicates if the rule instance is applicable for a specific hint<br>
   * Is required when checkHintApplicability is true
   * @abstract
   */
  static isApplicableForHint(hint: string): boolean {
    throw new IllegalStateException(
      `Missing isApplicableForHint implementation on layout hint rule`,
    );
  }

  /**
   * Initialize the rule when isApplicableForAttribute is true and checkHintApplicability is false
   * @abstract
   */
  static initAttribute(attribute: AttributeType): ILayoutHintRule {
    throw new IllegalStateException(
      `Missing initAttribute implementation on layout hint rule`,
    );
  }

  /**
   * Initialize the rule when isApplicableForAttribute and isApplicableForHint are both true,
   * it receives the hint. Allows a rule to be instantiated multiple times for an attribute,
   * but once for each hint entry
   * @abstract
   */
  static initHint(hint: string): ILayoutHintRule {
    throw new IllegalStateException(
      `Missing initHint implementation on layout hint rule`,
    );
  }

  /**
   * Abstract method, implements process of layout hint rule,
   * use this to enhance the applicable attribute or the attribute collection
   * @abstract
   */
  process(attribute: AttributeModel, attributes: AttributeCollection): void {
    throw new IllegalStateException(
      `Missing process implementation on layout hint rule`,
    );
  }
}

export default BaseLayoutHintRule;
